/**
 * SETTLING WHAT A MERCHANT IS OWED (FR-U-TXN-006, FR-U-LQD-003).
 *
 * ## Both modes, both prices, before either is chosen
 *
 * The requirement is not "offer instant settlement". It is that the merchant sees what each option
 * costs before they pick, because instant is the platform lending them its own balance sheet for a
 * few days and charging for it. A screen showing one prominent button and a fee afterwards would
 * meet the letter of that and miss the point.
 *
 * So both cards render side by side, each with its own fee and its own arrival wording, and neither
 * is styled as the default.
 *
 * ## When instant is unavailable it is EXPLAINED, never hidden
 *
 * FR-U-LQD-002. The treasury refuses for genuinely different reasons and they have different routes
 * forward: no float means try later or take standard, an unset exposure cap means nobody has
 * authorised the platform to carry this yet, suspended means the platform has stopped instant
 * deliberately. Hiding the card collapses all of those into "there is only one option", and a
 * merchant who was using instant yesterday is left guessing.
 *
 * The price is still shown when it is refused, so they can see what they are missing.
 *
 * ## What this screen must not claim
 *
 * That standard settlement has arrived. The rail is a simulator (ADR 0003), so the backend marks it
 * `pending` and says the money is not paid until the rail confirms. This renders that verbatim
 * rather than showing a success tick.
 */

import { useCallback, useState } from 'react';
import {
  LipaApiError,
  api,
  type FloatAdvance,
  type SettlementQuote,
  type SettlementResult,
} from '@/lib/api';
import { ErrorState, LoadingCard } from '@/components/states';
import { useResource } from '@/lib/use-resource';

/** Why instant is off, in the merchant's terms rather than the treasury's. */
const REFUSAL_COPY: Record<string, string> = {
  insufficient_float:
    'We do not have enough available right now to advance this. Standard settlement is unaffected.',
  exposure_cap:
    'This would go past the limit on how much we advance in total. Standard settlement is unaffected.',
  suspended: 'We have paused instant settlement. Standard settlement is unaffected.',
  no_board_rate: 'We have not published a rate for this today.',
  board_withdrawn: 'The market moved past the bound today’s rate was set against.',
  stale_rates: 'Our rates are older than we are willing to price against. Try again shortly.',
  daily_volume_cap: 'Today’s instant settlement volume is used up.',
};

export default function SettlementPanel({ asset = 'KES' }: { asset?: string }) {
  const { data, loading, error, reload } = useResource<SettlementQuote>(
    () => api.settlementQuote(asset),
    [asset],
  );
  const advances = useResource<FloatAdvance[]>(() => api.merchantAdvances(), []);

  const [pending, setPending] = useState<'standard' | 'instant' | null>(null);
  const [result, setResult] = useState<SettlementResult | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);

  const settle = useCallback(
    async (mode: 'standard' | 'instant') => {
      setPending(mode);
      setRefusal(null);
      try {
        const settled = await api.settleMerchant(asset, mode);
        setResult(settled);
        reload();
        advances.reload();
      } catch (e) {
        // The backend's message names WHICH refusal and whether the standard path is still open.
        // Replacing it with our own wording would turn an actionable sentence into a generic one.
        setRefusal(e instanceof LipaApiError ? e.message : 'We could not settle that.');
      } finally {
        setPending(null);
      }
    },
    [asset, reload, advances],
  );

  if (loading) return <LoadingCard lines={4} />;

  // "Nothing to settle" arrives as a policy refusal, not an empty success, and it is not an error
  // state: it is the ordinary situation of having already been paid.
  if (error)
    return (
      <div className="rounded-card border border-border bg-surface p-5 text-sm shadow-card">
        <p className="font-medium text-ink">Nothing to settle right now</p>
        <p className="mt-1 text-ink-muted">
          Everything you have taken is either already settled or held while a dispute is looked at.
        </p>
        <button onClick={reload} className="mt-3 text-sm text-brand hover:underline">
          Check again
        </button>
      </div>
    );

  if (!data) return <ErrorState body="We could not load your settlement." onRetry={reload} />;

  return (
    <section className="space-y-4">
      <header className="flex items-baseline justify-between gap-3">
        <div>
          <h2 className="text-section-title text-ink">Settle your takings</h2>
          <p className="mt-0.5 text-sm text-ink-muted">
            {data.payment_count} payment{data.payment_count === 1 ? '' : 's'} ready to settle
          </p>
        </div>
        <p className="tabular text-lg font-semibold text-ink">
          {data.gross} {data.asset}
        </p>
      </header>

      {result && (
        <div className="rounded-card border border-success/30 bg-success/5 p-4 text-sm">
          <p className="font-medium text-ink">
            {result.mode === 'instant' ? 'Sent to your account' : 'Settlement scheduled'}
          </p>
          {/* Rendered VERBATIM. The backend is careful to say the rail has not confirmed, and a
              paraphrase here is exactly where that caveat would quietly go. */}
          <p className="mt-1 text-ink-muted">{result.note ?? 'It is on its way.'}</p>
          {result.fee && result.fee !== '0.00' && (
            <dl className="mt-3 grid grid-cols-3 gap-2 border-t border-border pt-3 text-xs">
              <div>
                <dt className="text-ink-subtle">Taken</dt>
                <dd className="tabular font-medium text-ink">{result.gross}</dd>
              </div>
              <div>
                <dt className="text-ink-subtle">Our fee</dt>
                <dd className="tabular font-medium text-ink">{result.fee}</dd>
              </div>
              <div>
                <dt className="text-ink-subtle">You received</dt>
                <dd className="tabular font-medium text-ink">{result.net}</dd>
              </div>
            </dl>
          )}
        </div>
      )}

      {refusal && (
        <div className="rounded-card border border-warning/30 bg-warning/5 p-3 text-sm text-ink">
          {refusal}
        </div>
      )}

      {/* THE TWO MODES. Neither is styled as the default: they are a genuine choice. */}
      <div className="grid gap-3 sm:grid-cols-2">
        <article className="flex flex-col rounded-card border border-border bg-surface p-4 shadow-card">
          <h3 className="text-sm font-medium text-ink">Standard</h3>
          <p className="mt-0.5 text-xs text-ink-muted">{data.standard.arrives}</p>
          <dl className="mt-3 flex-1 space-y-1.5 text-sm">
            <div className="flex justify-between">
              <dt className="text-ink-muted">Fee</dt>
              <dd className="tabular font-medium text-success">Free</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-ink-muted">You get</dt>
              <dd className="tabular font-semibold text-ink">{data.standard.net}</dd>
            </div>
          </dl>
          <button
            disabled={pending !== null}
            onClick={() => settle('standard')}
            className="mt-4 w-full rounded-field border border-border bg-surface px-4 py-2.5 text-sm font-medium text-ink transition hover:bg-surface-sunken disabled:opacity-40"
          >
            {pending === 'standard' ? 'Scheduling...' : 'Settle the standard way'}
          </button>
        </article>

        {data.instant && (
          <article
            className={`flex flex-col rounded-card border bg-surface p-4 shadow-card ${
              data.instant_available ? 'border-border' : 'border-border opacity-70'
            }`}
          >
            <div className="flex items-baseline justify-between gap-2">
              <h3 className="text-sm font-medium text-ink">Instant</h3>
              <span className="text-micro text-ink-subtle">{data.instant.fee_bps} bps</span>
            </div>
            <p className="mt-0.5 text-xs text-ink-muted">
              {data.instant_available ? data.instant.arrives : 'Not available right now'}
            </p>
            <dl className="mt-3 flex-1 space-y-1.5 text-sm">
              <div className="flex justify-between">
                <dt className="text-ink-muted">Fee</dt>
                <dd className="tabular font-medium text-ink">{data.instant.fee}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-ink-muted">You get</dt>
                <dd className="tabular font-semibold text-ink">{data.instant.net}</dd>
              </div>
            </dl>

            {/* Priced but refused: the reason, not a hidden card. */}
            {!data.instant_available && data.instant_refusal && (
              <p className="mt-3 rounded-field bg-surface-sunken p-2.5 text-xs text-ink-muted">
                {REFUSAL_COPY[data.instant_refusal] ??
                  'Instant settlement is not available right now. Standard settlement is unaffected.'}
              </p>
            )}

            <button
              disabled={!data.instant_available || pending !== null}
              onClick={() => settle('instant')}
              className="mt-4 w-full rounded-field bg-primary px-4 py-2.5 text-sm font-medium text-white transition hover:bg-primary-light disabled:opacity-40"
            >
              {pending === 'instant' ? 'Sending...' : 'Get paid now'}
            </button>
          </article>
        )}
      </div>

      {/*
        What instant actually is, said once and plainly. A merchant paying a fee should know they are
        paying for the platform to carry the gap, not for a faster computer.
      */}
      {data.instant && (
        <p className="text-xs text-ink-subtle">
          Instant settlement means we pay you from our own funds before the payments have cleared to
          us, and charge a fee for carrying that. Standard settlement waits for the money to reach
          us and is free.
        </p>
      )}

      {(advances.data?.length ?? 0) > 0 && (
        <div className="rounded-card border border-border bg-surface-sunken p-3.5 text-xs">
          <p className="font-medium text-ink">Advanced to you, not yet cleared to us</p>
          <ul className="mt-2 space-y-1">
            {advances.data?.map((a) => (
              <li key={a.id} className="flex justify-between text-ink-muted">
                <span>{new Date(a.advanced_at).toLocaleDateString()}</span>
                <span className="tabular">
                  {a.gross} {a.asset}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
